// @flow
import * as React from 'react';
import { Popover, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { PRODUCT_DETAIL } from '../../../../locales/components/namespaces';

type Dot = {
    DotYear?: string,
    Qty?: string | number,
    displayStock?: number | string,
    DiscountPCT?: string | number,
};

type DotsState = {
    anchorDotEl: null | HTMLElement,
    dots: null | Array<Dot>,
};

type Props = {
    dots: DotsState,
    handleDots: (next: DotsState) => void,
};

const DotsPopOver = ({ dots, handleDots }: Props) => {
    const { t } = useTranslation(PRODUCT_DETAIL);

    const handlePopoverClose = () => {
        handleDots({
            anchorDotEl: null,
            dots: null,
        });
    };

    const rows: Array<Dot> = Array.isArray(dots?.dots) ? dots.dots : [];

    const sortedRows = React.useMemo(
        () =>
            [...rows].sort((a, b) =>
                String(b?.DotYear ?? '').localeCompare(String(a?.DotYear ?? ''))
            ),
        [rows]
    );

    return (
        <Popover
            sx={{ pointerEvents: 'none' }}
            open={Boolean(dots?.anchorDotEl)}
            anchorEl={dots?.anchorDotEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            onClose={handlePopoverClose}
            disableRestoreFocus
            slotProps={{
                paper: {
                    sx: { backgroundColor: '#f6ecf8', p: 1.2, minWidth: 260 },
                },
            }}
        >
            {sortedRows.length > 0 ? (
                <Box>

                    {/* Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            fontSize: 12,
                            fontWeight: 600,
                            pb: 0.5,
                            borderBottom: '1px solid rgba(0,0,0,0.12)',
                        }}
                    >
                        <Box sx={{ width: 72 }}>{t('Year')}</Box>
                        <Box sx={{ width: 100 }}>{t('Quantity')}</Box>
                        <Box sx={{ width: 100 }}>{t('Extra Discount')}</Box>
                    </Box>

                    {/* Rows */}
                    {sortedRows.map((d, i) => {
                        const discount = Number(d?.DiscountPCT) || 0;
                        const discountDisplay = discount === 0 ? '-' : `${discount}%`;
                        const qty = Number(d?.Qty) || 0;
                        const ds = Number(d?.displayStock);
                        const displayValue =
                            Number.isFinite(ds) && qty > ds
                                ? `${ds}+`
                                : (qty ?? '');
                        return (
                            <Box
                                key={`${d?.DotYear ?? 'unknown'}-${i}`}
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    fontSize: 12,
                                    py: 0.4,
                                    borderBottom:
                                        i < sortedRows.length - 1
                                            ? '1px dashed rgba(0,0,0,0.08)'
                                            : 'none',
                                }}
                            >
                                <Box sx={{ width: 72 }}>{d?.DotYear ?? ''}</Box>
                                <Box sx={{ width: 100 }}>{displayValue}</Box>
                                <Box sx={{ width: 100 }}>{discountDisplay}</Box>
                            </Box>
                        );
                    })}
                </Box>
            ) : (
                <Typography variant="body2" sx={{ p: 0.5 }}>
                    {t('No DOT info')}
                </Typography>
            )}
        </Popover>
    );
};

export default DotsPopOver;
